import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { ErrorBoundary } from '@/shared/features/error/components/error-boundary/error-boundary';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag/use-feature-flag';
import { FEATURE_IDEAAS_HUB } from '@/shared/hooks/use-feature-flag/use-feature-flag.constants';
import { TCustomRouteObject } from '@/shared/types/router.types';

const IdeaasHubGuard: React.FC = () => {
  const { isFeatureEnabled } = useFeatureFlag();

  if (!isFeatureEnabled(FEATURE_IDEAAS_HUB)) {
    return <ErrorBoundary />;
  }

  return <Outlet />;
};

export const ideaasHubRoutes: TCustomRouteObject = {
  path: '/ideaas-hub',
  Component: IdeaasHubGuard,
  handle: {
    crumb: {
      label: 'ideaas-hub',
      localized: true,
    },
  },
  children: [
    {
      index: true,
      element: <Navigate to="home" replace />,
    },
    {
      // Pathless layout route: renders ContentLayout + IdeaasHub (RoutedTabs + Outlet)
      lazy: async () => {
        const { IdeaasHubRoute } = await import('./ideaas-hub');
        return { Component: IdeaasHubRoute };
      },
      children: [
        {
          path: 'home',
          lazy: async () => {
            const { HomeRoute } = await import('./home');
            return { Component: HomeRoute };
          },
        },
        {
          path: 'apps',
          lazy: async () => {
            const { AppsRoute } = await import('./apps');
            return { Component: AppsRoute };
          },
        },
        {
          path: 'desktops',
          lazy: async () => {
            const { DesktopsRoute } = await import('./desktops');
            return { Component: DesktopsRoute };
          },
        },
        {
          path: 'recordings',
          lazy: async () => {
            const { RecordingsRoute } = await import('./recordings');
            return { Component: RecordingsRoute };
          },
        },
      ],
    },
    {
      path: 'settings',
      handle: {
        crumb: {
          label: 'ideaas:hub.settings_title',
          localized: true,
        },
      },
      lazy: async () => {
        const { SettingsRoute } = await import('./settings');
        return { Component: SettingsRoute };
      },
    },
    {
      path: 'player/:recordingId',
      handle: {
        crumb: {
          label: 'ideaas:recordings.player_title',
          localized: true,
        },
      },
      lazy: async () => {
        const { RecordingPlayerRoute } = await import('./recording-player');
        return { Component: RecordingPlayerRoute };
      },
    },
  ],
};
